import { createContext, useContext, useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
  isLandingPage: boolean
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation()
  const isLandingPage = location.pathname === '/'
  
  // Get initial theme based on location
  const getInitialTheme = (): Theme => {
    const storageKey = isLandingPage ? 'landingTheme' : 'appTheme'
    const savedTheme = localStorage.getItem(storageKey) as Theme | null
    
    if (savedTheme) {
      return savedTheme
    }
    
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark'
    }
    
    return 'light'
  }

  const [theme, setTheme] = useState<Theme>(getInitialTheme)

  // Update theme when location changes
  useEffect(() => {
    const newIsLandingPage = location.pathname === '/'
    const storageKey = newIsLandingPage ? 'landingTheme' : 'appTheme'
    const savedTheme = localStorage.getItem(storageKey) as Theme | null
    
    if (savedTheme) {
      setTheme(savedTheme)
    } else {
      // If no saved theme for this context, use system preference
      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
      setTheme(prefersDark ? 'dark' : 'light')
    }
  }, [location])

  // Apply theme to document when it changes
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    
    // Save to localStorage based on context
    const storageKey = isLandingPage ? 'landingTheme' : 'appTheme'
    localStorage.setItem(storageKey, theme)
  }, [theme, isLandingPage])

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't explicitly set a preference for current context
      const storageKey = isLandingPage ? 'landingTheme' : 'appTheme'
      if (!localStorage.getItem(storageKey)) {
        setTheme(e.matches ? 'dark' : 'light')
      }
    }
    
    // Add listener for system preference changes
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
    
    // Fallback for older browsers
    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [isLandingPage])

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light')
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isLandingPage }}>
      {children}
    </ThemeContext.Provider>
  )
}